import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { folderService } from '../../services/folderService';
import { convertToPng } from '../../lib/ImageConversion';

export function useItemActions(item) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['items'] });

  const { mutateAsync: remove } = useMutation({
    mutationFn: () => folderService.trashItem(item.id),
    onSuccess: () => { invalidate(); toast.success('Item moved to trash'); },
    onError: () => toast.error('Failed to move item to trash'),
  });

  const { mutateAsync: restore } = useMutation({
    mutationFn: () => folderService.restoreItem(item.id),
    onSuccess: () => { invalidate(); toast.success('Item restored'); },
    onError: () => toast.error('Failed to restore item'),
  });

  const { mutateAsync: deletePermanently } = useMutation({
    mutationFn: () => folderService.deleteItem(item.id),
    onSuccess: () => { invalidate(); toast.success('Item permanently deleted'); },
    onError: () => toast.error('Failed to permanently delete item'),
  });

  const { mutateAsync: move } = useMutation({
    mutationFn: (folderId) => folderService.moveItem(item.id, folderId),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to move item'),
  });

  const { mutateAsync: pin } = useMutation({
    mutationFn: (pinned) => folderService.pinItem(item.id, pinned),
    onSuccess: () => { invalidate(); },
    onError: () => toast.error('Failed to pin item'),
  });

  const copy = async () => {
    if (!navigator.clipboard || typeof navigator.clipboard.write !== 'function') {
      toast.error('Clipboard write not supported');
      return;
    }

    try {
      // Build the blob promise without awaiting it first.
      // Passing a Promise<Blob> to ClipboardItem lets navigator.clipboard.write()
      // be called synchronously within the user gesture, which is required on
      // Chrome iOS (and some other browsers) to avoid NotAllowedError.
      const mimeType = item.getType() === 'image' ? 'image/png' : (item.contentType || 'text/plain');
      const blobPromise = (async () => {
        const response = await fetch(item.contentUrl);
        if (!response.ok) throw new Error(`Failed to fetch item: ${response.status}`);
        let blob = await response.blob();
        if (item.getType() === 'image' && !blob.type.includes('png') && !blob.type.includes('webp')) {
          blob = await convertToPng(blob);
        }
        return blob;
      })();

      await navigator.clipboard.write([new window.ClipboardItem({ [mimeType]: blobPromise })]);
      toast.success('Copied item to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy item to clipboard');
    }
  };

  return { copy, remove, restore, deletePermanently, move, pin };
}
